import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../hooks/useConfirm';

export interface MenuItem {
  path: string;
  icon: string;
  label: string;
  permissions?: string[];
}

export interface MenuSection {
  title: string;
  items: MenuItem[];
}

export function getMenuSections(): MenuSection[] {
  return [
    {
      title: 'OVERVIEW',
      items: [{ path: '/dashboard', icon: 'fa-gauge-high', label: 'Dashboard', permissions: ['dashboard:view'] }],
    },
    {
      title: 'BILLING',
      items: [
        { path: '/clients', icon: 'fa-building-user', label: 'Clients', permissions: ['clients:view'] },
        { path: '/billing-config', icon: 'fa-sliders', label: 'Billing Configuration', permissions: ['billing-config:view'] },
        { path: '/billing', icon: 'fa-file-invoice-dollar', label: 'Billing', permissions: ['billing:view'] },
      ],
    },
    {
      title: 'ACCOUNTS',
      items: [
        { path: '/expenses', icon: 'fa-receipt', label: 'Expenses', permissions: ['expenses:view'] },
        { path: '/recurring-expenses', icon: 'fa-rotate', label: 'Recurring Bills', permissions: ['expenses:view'] },
        { path: '/employees', icon: 'fa-users', label: 'Employees', permissions: ['employees:view'] },
        { path: '/payroll', icon: 'fa-money-check-dollar', label: 'Payroll', permissions: ['employees:view'] },
        { path: '/capital', icon: 'fa-sack-dollar', label: 'Capital & Investment', permissions: ['capital:view'] },
      ],
    },
    {
      title: 'INSIGHTS',
      items: [{ path: '/reports', icon: 'fa-chart-column', label: 'Reports', permissions: ['reports:view'] }],
    },
    {
      title: 'SYSTEM',
      items: [{ path: '/settings', icon: 'fa-gear', label: 'Settings' }],
    },
  ];
}

export default function Sidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, hasAnyPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    setIsCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
  }, []);

  // Open the drawer when the mobile hamburger (in Navbar) requests it
  useEffect(() => {
    const openMobile = () => setIsMobileOpen(true);
    window.addEventListener('sidebarMobileOpen', openMobile);
    return () => window.removeEventListener('sidebarMobileOpen', openMobile);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
    window.dispatchEvent(new Event('sidebarToggle'));
  };

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === '/billing' && location.pathname.startsWith('/billing/')) return true;
    return false;
  };

  return (
    <>
      {isMobileOpen && (
        <div
          className="fixed inset-0 top-[58px] bg-ink-950/40 backdrop-blur-[1px] z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-[58px] lg:top-[62.5px] bottom-0 z-50 flex flex-col bg-brand-950 border-r border-brand-900 transition-all duration-250 lg:translate-x-0 ${
          isCollapsed ? 'w-[68px]' : 'w-[232px]'
        } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className={`hidden lg:flex items-center border-b border-brand-900 ${isCollapsed ? 'justify-center px-2 py-2.5' : 'justify-between px-3 py-2.5'}`}>
          {!isCollapsed && (
            <span className="text-[10px] font-extrabold tracking-[0.16em] text-brand-300/70 uppercase">Navigation</span>
          )}
          <button
            onClick={toggleCollapse}
            className="w-8 h-8 rounded-md border border-brand-800 text-brand-300 hover:text-accent-400 hover:border-accent-500/40 hover:bg-brand-900 flex items-center justify-center transition-colors"
            aria-label="Toggle Sidebar"
          >
            <i className={`fas ${isCollapsed ? 'fa-angles-right' : 'fa-angles-left'} text-[10px]`}></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-2 py-3">
          {getMenuSections().map((section, idx) => {
            const visible = section.items.filter((item) => !item.permissions || hasAnyPermission(item.permissions));
            if (visible.length === 0) return null;
            return (
              <div key={section.title} className="mb-4">
                {!isCollapsed && (
                  <div className="text-[10px] font-extrabold text-brand-300/60 uppercase tracking-[0.14em] mb-1.5 px-2.5">
                    {section.title}
                  </div>
                )}
                {isCollapsed && idx > 0 && <div className="h-px bg-brand-900 mx-2 mb-2" />}
                <div className="space-y-0.5">
                  {visible.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`group flex items-center rounded-md transition-colors duration-150 ${
                          isCollapsed ? 'justify-center px-2 py-2.5' : 'px-2.5 py-2'
                        } ${active ? 'bg-brand-800 text-white shadow-soft' : 'text-brand-100/80 hover:bg-brand-900 hover:text-white'}`}
                        title={isCollapsed ? item.label : ''}
                      >
                        <i
                          className={`fas ${item.icon} ${isCollapsed ? 'text-sm' : 'text-[13px] w-4 mr-2.5'} ${
                            active ? 'text-accent-400' : 'text-brand-300 group-hover:text-accent-300'
                          }`}
                        ></i>
                        {!isCollapsed && (
                          <span className={`text-[13px] truncate ${active ? 'font-semibold' : 'font-medium'}`}>{item.label}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-2 border-t border-brand-900">
          <button
            onClick={handleLogout}
            className={`w-full rounded-md text-[13px] font-semibold transition-colors border border-brand-800 text-brand-100/80 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400/40 ${
              isCollapsed ? 'py-2.5 px-2' : 'py-2.5 px-3 flex items-center justify-center gap-2'
            }`}
            title="Sign out"
          >
            <i className="fas fa-right-from-bracket text-xs"></i>
            {!isCollapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      <ConfirmDialog />
    </>
  );
}
